/**
 * The MESSAGE_SYNC request rail — "reconcile this task from server truth".
 *
 * One shared entry, because the sync carries more than messages (model,
 * runtime, accessLevel, todos, outputs, seq), so every path that lands a task
 * on screen must request it; starving it is how the ModelSelector pill sat on
 * "…" (founder 07-21/07-22). De-duped over a short window because navigation
 * fires it from more than one seam (switchTask + the TaskView URL effect) in
 * the same tick.
 *
 * Its own module rather than taskStore.ts's top level: the retry pump is
 * module-lifetime mutable state (a timer + two Maps) whose only contract with
 * the store is `requestTaskSync` / `ackTaskSync`. taskStore re-exports both so
 * existing importers are unchanged.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { getSocket, ensureConnected } from "./socketService";

const SYNC_REQUEST_DEDUPE_MS = 1_500;
const lastSyncRequestAt = new Map<string, number>();
// LEVEL-TRIGGERED sync (incident b6c10324, 2026-08-12): a deploy-skew hard
// reload mounted TaskView before the socket handshake finished; the old
// `if (!socket?.connected) return` silently dropped the only sync a freshly-
// loaded task page would ever ask for, so it rendered blank for the whole 51s
// turn. A request now stays PENDING until the task's MESSAGE_SYNC actually
// lands (ackTaskSync) — covering both the not-yet-connected mount and an emit
// lost to a server dying mid-deploy — re-asked on a pump, bounded so a server
// that never answers at all can't be spammed forever. (Any server response
// acks: handleMessageSync fires for error syncs too, except a TASK_NOT_FOUND
// for a task this client minted moments ago, which is "not yet", not an answer.)
const PENDING_SYNC_RETRY_MS = 2_000;
const PENDING_SYNC_MAX_ATTEMPTS = 15;
const pendingSyncAttempts = new Map<string, number>();
let pendingSyncTimer: ReturnType<typeof setTimeout> | null = null;

function emitTaskSync(socket: Pick<Socket, "emit">, taskId: string): void {
  const last = lastSyncRequestAt.get(taskId);
  const now = Date.now();
  if (last !== undefined && now - last < SYNC_REQUEST_DEDUPE_MS) return;
  lastSyncRequestAt.set(taskId, now);
  if (lastSyncRequestAt.size > 200) {
    for (const [k, v] of lastSyncRequestAt) {
      if (now - v > SYNC_REQUEST_DEDUPE_MS) lastSyncRequestAt.delete(k);
    }
  }
  socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
}

function pumpPendingSyncs(): void {
  if (pendingSyncTimer !== null || pendingSyncAttempts.size === 0) return;
  pendingSyncTimer = setTimeout(() => {
    pendingSyncTimer = null;
    const socket = getSocket();
    for (const [taskId, attempts] of pendingSyncAttempts) {
      if (attempts >= PENDING_SYNC_MAX_ATTEMPTS) {
        pendingSyncAttempts.delete(taskId);
        console.warn("[TaskSync] giving up on unacked sync for", taskId);
        continue;
      }
      if (socket?.connected) {
        pendingSyncAttempts.set(taskId, attempts + 1);
        emitTaskSync(socket, taskId);
      }
    }
    if (socket && !socket.connected) ensureConnected();
    pumpPendingSyncs();
  }, PENDING_SYNC_RETRY_MS);
}

export function requestTaskSync(taskId: string): void {
  const socket = getSocket();
  if (socket?.connected) {
    emitTaskSync(socket, taskId);
  } else {
    ensureConnected();
  }
  if (!pendingSyncAttempts.has(taskId)) pendingSyncAttempts.set(taskId, 0);
  pumpPendingSyncs();
}

/** A MESSAGE_SYNC for this task landed — the pending request is satisfied. */
export function ackTaskSync(taskId: string): void {
  pendingSyncAttempts.delete(taskId);
}
